import { useState, useCallback } from 'react';
import pick from 'lodash/pick';

import { CustomReportParamTypes } from '../../shared/customReports/constants';
import { getParamNamesForQuery } from '../../shared/customReports/helpers';

export const useCustomReportValidation = ({
  customReport,
  customReportSetReports,
  isReportSetOpen,
  customReportQueryParams,
  verifyParamOptionsQuery,
  paramMapping,
}) => {
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [pendingExecution, setPendingExecution] = useState(null);

  const getValidatorParams = useCallback(() => {
    if (!customReport) {
      return [];
    }

    if (isReportSetOpen && customReportSetReports && customReportSetReports.length > 0) {
      const allValidators = [];
      const seenValidators = new Set();

      customReportSetReports.forEach(report => {
        if (report.params) {
          Object.values(report.params).forEach(param => {
            if (param.type === CustomReportParamTypes.validator) {
              const validatorKey = `${param.name}_${param.query}`;
              if (!seenValidators.has(validatorKey)) {
                seenValidators.add(validatorKey);
                allValidators.push(param);
              }
            }
          });
        }
      });

      return allValidators;
    }

    if (customReport.params) {
      return Object.values(customReport.params).filter(
        param => param.type === CustomReportParamTypes.validator
      );
    }

    return [];
  }, [customReport, customReportSetReports, isReportSetOpen]);

  const runValidators = useCallback(onSuccess => {
    const validatorParams = getValidatorParams();

    if (validatorParams.length === 0) {
      onSuccess();
      return;
    }

    let validatorsToCheck = validatorParams.length;
    let hasValidationError = false;
    let validationErrorMessage = '';

    const onValidationSuccess = response => {
      validatorsToCheck--;

      if (response && response.length > 0) {
        const validationResult = response[0].validationResult
          || response[0].validation_result
          || response[0].VALIDATION_RESULT;

        if (validationResult && validationResult !== 'OK') {
          hasValidationError = true;
          validationErrorMessage = validationResult;
        }
      }

      if (validatorsToCheck === 0) {
        if (hasValidationError) {
          setShowValidationModal(true);
          setValidationMessage(validationErrorMessage);
          setPendingExecution(() => onSuccess);
        } else {
          onSuccess();
        }
      }
    };

    const onValidationFailure = () => {
      validatorsToCheck--;

      if (validatorsToCheck === 0) {
        if (!hasValidationError) {
          onSuccess();
        }
      }
    };

    validatorParams.forEach(validatorParam => {
      const paramNames = getParamNamesForQuery(validatorParam.query);

      let validationParams;
      if (isReportSetOpen && paramMapping && customReportSetReports && customReportSetReports.length > 0) {
        validationParams = paramNames.reduce((result, paramName) => {
          for (const report of customReportSetReports) {
            const mappedParamId = paramMapping[report.reportSetId]?.[paramName];
            if (mappedParamId && customReportQueryParams[mappedParamId]) {
              result[paramName] = customReportQueryParams[mappedParamId];
              break;
            }
          }
          return result;
        }, {});
      } else {
        validationParams = pick(customReportQueryParams, paramNames);
      }

      verifyParamOptionsQuery({
        query: validatorParam.query,
        params: validationParams,
        onSuccess: onValidationSuccess,
        onFailure: onValidationFailure,
      });
    });
  }, [
    getValidatorParams,
    customReportQueryParams,
    verifyParamOptionsQuery,
    isReportSetOpen,
    paramMapping,
    customReportSetReports,
  ]);

  const handleValidationCancel = useCallback(() => {
    setShowValidationModal(false);
    setValidationMessage('');
    setPendingExecution(null);
  }, []);

  const handleValidationProceed = useCallback(() => {
    const callback = pendingExecution;
    setShowValidationModal(false);
    setValidationMessage('');
    setPendingExecution(null);

    if (callback) {
      callback();
    }
  }, [pendingExecution]);

  return {
    showValidationModal,
    validationMessage,
    runValidators,
    handleValidationCancel,
    handleValidationProceed,
  };
};

export default useCustomReportValidation;
